import { useState, useEffect } from "react";
import { Space, Button, Row, Col, Input, Drawer, Select, Form, notification } from "antd";
import {
  useCreatePartnerMutation,
  useGetPartnerQuery,
  useUpdatePartnerMutation
} from "../../redux/services/partnerApi";
import ErrorDetails from "../../components/ErrorDetails";

const { Option } = Select;

const PartnerForm = (props: any) => {
  const { isOpen, setOpen, partnerId, refetch } = props;

  const [api, contextHolder] = notification.useNotification();

  const [form] = Form.useForm();

  const { data: partnerData, isLoading } = useGetPartnerQuery(partnerId);
  const selectedPartnerData: any = partnerData?.partner;

  const [createPartner, { isLoading: createInProgress }] = useCreatePartnerMutation();

  const [updatePartner, { isLoading: updateInProgress }] = useUpdatePartnerMutation();

  useEffect(() => {
    if (selectedPartnerData) {
      form.setFieldsValue({
        ...selectedPartnerData
      });
    }
    return () => {
      form.resetFields();
    };
  }, [selectedPartnerData]);

  const onFinish = (values: any) => {
    if (partnerId) {
      onUpdatePartner(values);
    } else {
      addPartner(values);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const addPartner = async (values: object) => {
    try {
      await createPartner(values).unwrap();
      api.success({
        message: "Create operation successful",
        placement: "top"
      });
      form.resetFields();
      setOpen(false);
      refetch();
    } catch (error: any) {
      api.info({
        message: "Create operation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
    }
  };

  const onUpdatePartner = async (values: object) => {
    try {
      await updatePartner({ id: partnerId, partner: values }).unwrap();
      api.success({
        message: "Update operation successful",
        placement: "top"
      });
      form.resetFields();
      setOpen(false);
      refetch();
    } catch (error: any) {
      api.info({
        message: "Update operation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
    }
  };

  return (
    <Drawer
      title={`${partnerId ? "Update" : "Create"} Partner`}
      placement="right"
      closable={true}
      onClose={() => {
        form.resetFields();
        setOpen(!isOpen);
      }}
      open={isOpen}
      key={"partner-drawer"}
    >
      {contextHolder}
      <Form
        name="partner_form"
        form={form}
        initialValues={{ remember: true }}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
        disabled={updateInProgress || createInProgress || isLoading}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[
            { required: true, message: "Please input name" },
            { min: 3, message: "Name must be minimum 3 characters." },
            { max: 30, message: "Name must be maximum 30 characters." }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Company Name"
          name="company_name"
          rules={[
            { min: 3, message: "Company Name must be minimum 3 characters." },
            { max: 50, message: "Company Name must be maximum 50 characters." }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Partner Code"
          name="code"
          rules={[
            { min: 2, message: "Code must be minimum 2 characters." },
            { max: 5, message: "Code must be maximum 5 characters." }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Contact Person"
          name="contact_person"
          rules={[
            { min: 3, message: "Contact Person Name must be minimum 3 characters." },
            { max: 30, message: "Contact Person Name must be maximum 30 characters." }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Contact Number"
          name="contact_number"
          rules={[
            { min: 10, message: "Contact Number must be minimum 10 digit." },
            { max: 10, message: "Contact Number be maximum 10 digit." },
            { pattern: /^[0-9]+$/, message: "Contact Number must contain only numbers." }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Another Contact Number"
          name="alt_contact_number"
          rules={[
            { min: 10, message: "Another Contact Number must be minimum 10 digit." },
            { max: 10, message: "Another Contact Number be maximum 10 digit." },
            { pattern: /^[0-9]+$/, message: "Mobile No. must contain only numbers." }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="status"
          label="Status"
          rules={[{ required: true, message: "Please select status" }]}
        >
          <Select
            placeholder="Select status"
            showSearch
            optionFilterProp="children"
            filterOption={(input, option: any) =>
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
            <Option value="disabled">Disabled</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="GST Number"
          name="gst"
          rules={[
            { min: 15, message: "GST Number must be minimum 15 digit." },
            { max: 15, message: "GST Number be maximum 15 digit." }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Billing Address"
          name="billing_address"
          rules={[
            { min: 3, message: "Billing Address must be minimum 3 digit." },
            { max: 255, message: "Billing Address be maximum 255 digit." }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: 0, span: 24 }}>
          <Row>
            <Col>
              <Space size={"middle"}>
                <Button
                  type="default"
                  onClick={() => {
                    form.resetFields();
                    setOpen(!isOpen);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={updateInProgress || createInProgress}
                >
                  {partnerId ? "Update" : "Add"}
                </Button>
              </Space>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default PartnerForm;
